<template>
	<div class="idor">
		<div class="idor__elm">
			<!-- //////////////////// -->
			<!-- HEADER -->
			<div @click="onClickShowOptions" class="idor__elm-header">
				<div class="idor__elm-header-text">Опции</div>
				<div
					:class="
						isShowOptions
							? 'idor__elm-header-icon-down'
							: 'idor__elm-header-icon-up'
					"
				>
					<IconChevronDown />
				</div>
			</div>

			<!-- //////////////////// -->
			<!-- BODY -->
			<div class="idor__elm-body" v-if="isShowOptions">
				<!-- ~~~~~~~~~~~~~~~~~~~~ -->
				<!-- SWITCHER -->
				<div class="idor__elm-body-switcher">
					<div
						@click="paymentBeforeInstance"
						:class="
							isChecked
								? 'idor__elm-body-switcher-on-left'
								: 'idor__elm-body-switcher-off'
						"
					>
						Оплата до инстанса
					</div>

					<div
						@click="paymentPerMonth"
						:class="
							!isChecked
								? 'idor__elm-body-switcher-on-right'
								: 'idor__elm-body-switcher-off'
						"
					>
						Оплата на месяц
					</div>
				</div>

				<!-- ~~~~~~~~~~~~~~~~~~~~ -->
				<!-- OPTIONS LIST -->
				<div v-for="(item, index) in optionList" :key="index">
					<div
						v-if="excludePaid(item.id)"
						class="idor__elm-body-item"
					>
						<div class="idor__elm-body-item-checkbox">
							<UiSwitcher
								:is-array-items="true"
								:name="`${item.name}`"
								:item-id="Number(item.id)"
								v-on:checked-item="checked"
							/>
						</div>
						<div class="idor__elm-body-item-text">
							{{ item.name }}
						</div>

						<div class="idor__elm-body-item-text">
							{{ item.cost }} руб.
						</div>
					</div>
				</div>

				<!-- ~~~~~~~~~~~~~~~~~~~~ -->
				<!-- INFORMATION -->
				<div v-if="isChecked" class="idor__elm-body-info">
					Опции будут оплачены до даты окончания инстанса.
				</div>
				<div v-if="!isChecked" class="idor__elm-body-info">
					Опции будут оплачены на месяц, а инстанс будет автоматически
					продлён.
				</div>

				<!-- ~~~~~~~~~~~~~~~~~~~~ -->
				<!-- Список выбранных опций -->
				<div v-if="!isPayMonthDisable" class="idor__elm-body-footer">
					<!-- Список -->
					<div class="idor__elm-body-footer-option">
						<!-- //////////////// -->
						<!-- ОПЦИИ -->
						<div class="idor__elm-body-footer-option-label">
							Опции:
						</div>

						<div v-for="(option, idx) in optionsMap" :key="idx">
							<div
								v-for="(item, index) in optionList"
								:key="index"
							>
								<div
									v-if="item.id === option[0]"
									class="idor__elm-body-footer-option-item"
								>
									<div
										class="idor__elm-body-footer-option-item-label"
									>
										{{ item.name }}:
									</div>
									<div
										class="idor__elm-body-footer-option-item-label"
									>
										{{ item.cost }}
										руб.
									</div>
									<div
										class="idor__elm-body-footer-option-item-label"
									>
										до {{ instanceExpired }}
									</div>
								</div>
							</div>
						</div>

						<!-- //////////////// -->
						<!-- Продление Инстанса -->
						<div
							v-if="!isChecked"
							class="idor__elm-body-footer-option-label-ins"
						>
							<div
								class="idor__elm-body-footer-option-label-ins-txt"
							>
								Продление инстанса:
							</div>
							<div
								class="idor__elm-body-footer-option-label-ins-cost"
							>
								{{ channelCost() }} руб.
							</div>
							<div
								class="idor__elm-body-footer-option-label-ins-cost"
							>
								до {{ instanceExpired }}
							</div>
						</div>

						<!-- //////////////// -->
						<!-- Продление купленных опций -->
						<div v-if="!isChecked && isPaidOptionsNeedRenew">
							<div class="idor__elm-body-footer-option-label">
								Продление опций:
							</div>

							<!-- Список опций, которые будут продлены -->
							<div
								v-for="(item, index) in paidOptions"
								:key="index"
							>
								<div
									v-if="item.need_renew === 1"
									class="idor__elm-body-footer-option-item"
								>
									<div
										class="idor__elm-body-footer-option-item-label"
									>
										{{
											getOptionById(item.option_id).name
										}}:
									</div>
									<div
										class="idor__elm-body-footer-option-item-label"
									>
										{{ recalcOptionCost(item.option_id) }}

										руб.
									</div>
									<div
										class="idor__elm-body-footer-option-item-label"
									>
										до {{ instanceExpired }}
									</div>
								</div>
							</div>
						</div>
					</div>

					<!-- Button -->
					<div class="idor__elm-body-footer-btn">
						<UiButton
							@click="onClickPay"
							:label="`Оплатить ${payBtnInfo} руб`"
							background-color="var(--theme-color)"
							border-color="var(--theme-color)"
							color="rgb(255, 255, 255)"
						/>
						<div v-show="false">
							{{ paidOptionsCalculate }}
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";
import IconChevronDown from "@/components/icons/IconChevronDown.vue";
import { IPaidOptions } from "@/interfaces/IPaidOptions";
import { mapActions, mapGetters } from "vuex";
import UiSwitcher from "@/components/ui/UiSwitcher.vue";
import { IOptions } from "@/interfaces/IOptions";
import { IInstanceDetail } from "@/interfaces/IInstanceDetail";
import UiButton from "@/components/ui/UiButton.vue";
import dayjs from "dayjs";

/**
 * Redesign of the InstanceDetailOptions component
 */
@Options({
	components: { UiButton, UiSwitcher, IconChevronDown },
	props: {
		paidOptions: { type: Array, default: [] },
	},

	computed: {
		...mapGetters("instanceModule", {
			optionList: "optionListGetters",
			instance: "getInstance",
		}),

		...mapGetters("paymentModule", {
			payOptions: "payOptionsGetters",
			payChannelDetail: "payChannelDetailGetters",
		}),
	},

	methods: {
		...mapActions("paymentModule", ["payOptionsAction"]),
	},
})
export default class InstanceDetailOptionsR extends Vue {
	public paidOptions: Array<IPaidOptions>;

	public optionsMap = new Map();
	public options: Array<number> = [];
	public isPayMonthDisable = true;
	public isPayMonthChecked = false;
	public payInstanceExpired = "";
	public billingPeriod = 0;
	public surcharge = 0;
	public payBtnInfo = 0;
	public sumOptions = 0;

	public surchargeOptionList: Array<{ name: string; cost: number }>;

	public isChecked = true;

	// ************************************************************
	// Define mapGetters
	public optionList: Array<IOptions>;
	public payOptions: { createdId: number; url: string };
	public instance: IInstanceDetail;
	public payChannelDetail: {
		tariff: {
			userId: number;
			name: string;
			cost: number;
			type: number;
			partnerId: number;
		};
	};

	// ************************************************************
	// Define mapActions

	public payOptionsAction!: (payload: {
		optionsIds: Array<number>;
		instanceId: number;
		isPayMonth: boolean;
	}) => Promise<void>;

	public isShowOptions = false;

	/**
	 * Показать список опций
	 */
	public async onClickShowOptions() {
		this.isShowOptions = !this.isShowOptions;
		this.isPayMonthChecked = false;
		this.isPayMonthDisable = true;
		this.optionsMap.clear();
	}

	/**
	 * Возвращает true, если имеются купленные опции с автопродлением
	 */
	public get isPaidOptionsNeedRenew(): boolean {
		for (let idx = 0; idx < this.paidOptions.length; idx++) {
			if (this.paidOptions[idx].need_renew === 1) {
				return true;
			}
		}

		return false;
	}

	/**
	 * Обработка выбранных опций
	 * @param val
	 */
	public checked(val: { id: number; name: string; isActive: boolean }) {
		if (val.isActive) {
			this.optionsMap.set(val.id, val.id);
		} else {
			this.optionsMap.delete(val.id);
		}

		// Количество отмеченных чекбоксов (Покупка опций)
		if (this.optionsMap.size === 0) {
			this.sumOptions = 0;
		}

		this.options = [];

		let sum = 0;
		this.optionsMap.forEach((item) => {
			const opt = this.getOptionById(item) as IOptions;
			sum += opt.cost;
		});

		// Если опция не выбрана, то checkbox (Хочу продлить на месяц) скрываем
		this.isPayMonthDisable = this.optionsMap.size === 0;

		if (this.isPayMonthDisable) {
			this.surcharge = 0;
			this.isPayMonthChecked = false;
			this.isChecked = true;
		}

		this.payBtnInfo = sum + Math.round(this.surcharge) + this.sumOptions;
	}

	/**
	 * Обработка события (checkbox - автопродление)
	 */
	public get paidOptionsCalculate() {
		this.calculate();
		return this.paidOptions;
	}

	/**
	 * Доплата за опции
	 */
	public calculateForOptions(expired: number, DAYS_IN_MONTH = 31) {
		const surchargeOptionArr: Array<{ name: string; cost: number }> = [];

		for (let idx = 0; idx < this.paidOptions.length; idx++) {
			if (this.paidOptions[idx].need_renew === 1) {
				const optionId = this.paidOptions[idx].option_id;
				const option = this.getOptionById(optionId) as IOptions;

				// Вычитаем разницу, и получаем количество дней насколько будет продление опции
				const days = expired - this.paidOptions[idx].expire_stamp;
				const billingPeriod = Number(dayjs(days).format("D")) - 1;

				// Расчёт стоимости доплаты за опцию
				const surchargeOption =
					(option.cost / DAYS_IN_MONTH) * billingPeriod;

				surchargeOptionArr.push({
					name: option.name,
					cost: surchargeOption,
				});

				this.sumOptions += Math.round(surchargeOption);
			}
		}

		this.surchargeOptionList = surchargeOptionArr;

		return this.sumOptions;
	}

	/**
	 * Пересчёт, если меняем состояние чекбокса - автопродление
	 */
	public calculate() {
		// Дата на месяц вперёд
		const expired = dayjs().add(1, "month").unix() * 1000;
		this.payInstanceExpired = dayjs(expired).format("DD MMMM HH:mm");

		// Вычитаем разницу, и получаем количество дней насколько будет продление канала
		const days = expired - this.instance.expire_payment_stamp;
		this.billingPeriod = Number(dayjs(days).format("D")) - 1;

		const DAYS_IN_MONTH = dayjs(new Date()).daysInMonth();

		// Расчёт стоимости доплаты за канал
		this.surcharge =
			(this.payChannelDetail.tariff.cost / DAYS_IN_MONTH) *
			this.billingPeriod;

		this.options = [];

		let sum = 0;
		this.optionsMap.forEach((item) => {
			const opt = this.getOptionById(item) as IOptions;
			sum += opt.cost;
		});

		if (this.isPayMonthChecked) {
			this.sumOptions = 0;

			// Также нужно учитывать купленные опции, если у них установлен флаг автопродления
			// Получаем данные купленных опций с флагом 1 (true)
			this.sumOptions = this.calculateForOptions(expired, DAYS_IN_MONTH);

			this.payBtnInfo =
				sum + Math.round(this.surcharge) + this.sumOptions;

			return this.payBtnInfo;
		}

		this.payBtnInfo = sum;

		return this.payBtnInfo;
	}

	/**
	 * Возвращает информацию для опции по id
	 * @param optionId
	 */
	public getOptionById(optionId: number): IOptions {
		for (let idx = 0; idx < this.optionList.length; idx++) {
			if (this.optionList[idx].id === optionId) {
				return this.optionList[idx];
			}
		}

		return {
			id: 0,
			cost: 0,
			description: "",
			is_archive: 0,
			name: "",
			tariff_id: 0,
			update_stamp: "",
		};
	}

	/**
	 * Исключаем уже купленные опции
	 * @param optionId
	 */
	public excludePaid(optionId: number) {
		for (let idx = 0; idx < this.paidOptions.length; idx++) {
			if (this.paidOptions[idx].option_id === optionId) {
				return false;
			}
		}

		return true;
	}

	/**
	 * Обработка кнопки - Оплатить
	 */
	public async onClickPay() {
		const instanceId = Number(this.$route.params.id);
		this.options = [];

		this.optionsMap.forEach((item) => {
			this.options.push(item);
		});

		await this.payOptionsAction({
			optionsIds: this.options,
			instanceId: instanceId,
			isPayMonth: this.isPayMonthChecked, // С продлением на месяц
		});

		if (this.payOptions.url !== "") {
			window.location.href = this.payOptions.url;
		}
	}

	/**
	 * Оплата до инстанса
	 */
	public paymentBeforeInstance() {
		this.isChecked = true;
		this.isPayMonthChecked = false;
		this.calculate();
	}

	/**
	 * Оплата на месяц
	 */
	public paymentPerMonth() {
		this.isChecked = false;
		this.isPayMonthChecked = true;
		this.calculate();
	}

	/**
	 * Дата истечения инстанса
	 */
	public get instanceExpired() {
		if (this.isPayMonthChecked) {
			const expired = dayjs().add(1, "month").unix() * 1000;
			return dayjs(expired).format("DD.MM.YYYY");
		}
		return dayjs(this.instance.expire_payment_stamp).format("DD.MM.YYYY");
	}

	/**
	 * Стоимость опции в зависимости от количества дней
	 * @param item
	 */
	public optionCost(item: IOptions) {
		if (this.isPayMonthChecked) {
			const expired = dayjs().add(1, "month").unix() * 1000;

			// Вычитаем разницу, и получаем количество дней насколько будет продление опции
			let days = 0;
			for (let idx = 0; idx < this.paidOptions.length; idx++) {
				if (item.id === this.paidOptions[idx].option_id) {
					days = expired - this.paidOptions[idx].expire_stamp;
				}
			}

			const billingPeriod = Number(dayjs(days).format("D")) - 1;
			const DAYS_IN_MONTH = dayjs(new Date()).daysInMonth();

			// Расчёт стоимости доплаты за опцию
			const surcharge = (item.cost / DAYS_IN_MONTH) * billingPeriod;

			return Math.round(surcharge);
		}

		return item.cost;
	}

	/**
	 * Доплата за канал
	 */
	public channelCost() {
		if (this.isPayMonthChecked) {
			const expired = dayjs().add(1, "month").unix() * 1000;

			// Вычитаем разницу, и получаем количество дней насколько будет продление канала
			const days = expired - this.instance.expire_payment_stamp;
			const billingPeriod = Number(dayjs(days).format("D")) - 1;

			const DAYS_IN_MONTH = dayjs(new Date()).daysInMonth();

			// Расчёт стоимости доплаты за канал
			const surcharge =
				(this.payChannelDetail.tariff.cost / DAYS_IN_MONTH) *
				billingPeriod;

			return Math.round(surcharge);
		}

		return 0;
	}

	/**
	 * Показываем стоимость опции в шаблоне
	 * @param optionId
	 */
	public recalcOptionCost(optionId: number) {
		for (let idx = 0; idx < this.paidOptions.length; idx++) {
			if (
				this.paidOptions[idx].option_id === optionId &&
				this.paidOptions[idx].need_renew === 1
			) {
				const option = this.getOptionById(optionId);
				return this.optionCost(option);
			}
		}

		return 0;
	}
}
</script>

<style scoped lang="scss">
.idor {
	display: flex;
	justify-content: center;

	&__elm {
		display: flex;
		flex-direction: column;
		flex-grow: 1;
		max-width: 649px;
		background: rgb(255, 255, 255);
		border-radius: 12px;
		box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.02);
	}

	&__elm-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 24px;
		user-select: none;
		color: rgb(49, 49, 50);
		font-size: 20px;
		font-weight: 600;
		cursor: pointer;
	}

	&__elm-header-icon-up {
		transform: rotate(0deg);
	}

	&__elm-header-icon-down {
		transform: rotate(180deg);
	}

	&__elm-body {
		display: flex;
		flex-direction: column;
		padding: 12px 24px 0;
	}

	// Switcher
	&__elm-body-switcher {
		margin-bottom: 32px;
		display: flex;
		border: 2px solid rgb(223, 230, 238);
		border-radius: 12px;
		background: rgb(244, 246, 250);
		text-align: center;
	}

	&__elm-body-switcher-on-left {
		padding: 12px 14px;
		cursor: pointer;
		color: rgb(49, 49, 50);
		background: rgb(255, 255, 255);
		border-radius: 12px;
		box-shadow: 4px 0 12px rgba(0, 0, 0, 0.09);
		flex-grow: 1;

		// Анимация
		animation-duration: 0.6s;
		animation-name: appearance-animation-modal;
	}

	&__elm-body-switcher-on-right {
		padding: 12px 14px;
		cursor: pointer;
		color: rgb(49, 49, 50);
		background: rgb(255, 255, 255);
		border-radius: 12px;
		box-shadow: -4px 0 12px rgba(0, 0, 0, 0.09);
		flex-grow: 1;

		// Анимация
		animation-duration: 0.6s;
		animation-name: appearance-animation-modal;
	}

	&__elm-body-switcher-off {
		padding: 12px 14px;
		cursor: pointer;
		border-radius: 8px;
		color: rgb(119, 121, 124);
		flex-grow: 1;
	}

	&__elm-body-item {
		display: grid;
		grid-template-columns: 35px 0.5fr 1fr;
		margin-bottom: 16px;
	}

	&__elm-body-item-text {
		color: rgb(49, 49, 50);
	}

	&__elm-body-info {
		margin: 12px 0 24px;
		padding: 16px;
		background: rgb(255, 243, 222);
		color: rgb(49, 49, 50);
		font-size: 14px;
		border-radius: 16px;
	}

	// Footer

	&__elm-body-footer-option-label {
		padding: 4px 0 6px;
		color: rgb(49, 49, 50);
		font-weight: 600;
	}

	&__elm-body-footer-option-item {
		display: grid;
		grid-template-columns: 2fr 0.9fr 1.1fr;
		padding: 3px 0 3px;
		color: rgb(49, 49, 50);
	}

	&__elm-body-footer-option-item-label {
		padding: 0 0 6px;
	}

	&__elm-body-footer-option-label-ins-txt {
		color: rgb(49, 49, 50);
		font-weight: 600;
	}

	&__elm-body-footer-option-label-ins-cost {
		color: rgb(49, 49, 50);
	}

	@media screen and (min-width: 768px) {
		&__elm-body-footer {
			padding: 0 0 24px;
			display: flex;
			justify-content: space-between;
			align-items: flex-end;
		}

		&__elm-body-footer-btn {
			display: flex;
			align-items: flex-end;
		}

		&__elm-body-footer-option-label-ins {
			display: grid;
			grid-template-columns: 2fr 0.9fr 1.1fr;
			padding: 12px 0 12px;
		}
	}

	@media screen and (max-width: 768px) {
		&__elm-header {
			padding: 16px;
		}

		&__elm-body {
			padding: 0 16px 16px;
		}

		&__elm-footer {
			padding: 0 12px 12px;
		}

		&__elm-body-footer-option-item {
			display: grid;
			grid-template-columns: 1fr 1fr 1fr;
			padding: 3px 0 3px;
			color: rgb(49, 49, 50);
		}

		&__elm-body-footer-option-label-ins {
			display: grid;
			grid-template-columns: 1fr 1fr 1fr;
			padding: 12px 0 12px;
		}
	}
}

@keyframes appearance-animation-modal {
	0% {
		opacity: 0.5;
	}
	100% {
		opacity: 1;
	}
}
</style>
